/**
 * timeWindow (#2436) — pure slice math for the shared stock-app-style zoom/pan
 * behaviour layered over the CATEGORICAL-axis app time-series charts. Two
 * adopters today: ernteprognose and liquiditaetsplanung. (Preisspiegel was the
 * third until #2543 moved it to the numeric ``timeDomain`` family; it no longer
 * imports anything from this module.)
 *
 * Those charts are bespoke recharts ``LineChart``s / ``ComposedChart``s (#2544-B)
 * on CATEGORICAL (string) x-axes, so recharts' numeric-``domain`` windowing does
 * not apply. The clean,
 * testable model is therefore an INDEX window ``[start, end]`` (inclusive) into
 * the already-shaped, already-sorted rows array: zoom narrows/widens the visible
 * index span, pan shifts it, and the view slices the rows before handing them to
 * recharts (windowed rendering — also what keeps panning smooth over a large
 * series). Because each chart's full rows array already spans
 * ``[earliest data … today + forecast horizon]``, clamping the index window to
 * ``[0, rowCount − 1]`` is exactly the issue's "operate strictly within
 * [earliest data … today + forecast horizon]" bound — no date math needed.
 *
 * All functions are pure (no React, no recharts — recharts does not render under
 * jsdom) so the focal-zoom invariance and the pan clamps are unit-tested here.
 */

/** An inclusive index window ``[start, end]`` into a sorted rows array. */
export interface IndexWindow {
  start: number;
  end: number;
}

/** A line needs at least two points; the zoom-in floor never drops below this. */
export const MIN_VISIBLE_ROWS = 2;

/** Number of rows a window shows (inclusive of both ends). */
export function visibleCount(w: IndexWindow): number {
  return w.end - w.start + 1;
}

/** The default "fit" window: the whole rows array. */
export function fitWindow(rowCount: number): IndexWindow {
  if (rowCount <= 0) return { start: 0, end: 0 };
  return { start: 0, end: rowCount - 1 };
}

/** True when the window covers the entire rows array (nothing to reset/zoom-out to). */
export function isFit(w: IndexWindow, rowCount: number): boolean {
  return w.start <= 0 && w.end >= rowCount - 1;
}

/**
 * Clamp a (possibly out-of-bounds or degenerate) window into ``[0, rowCount−1]``,
 * PRESERVING its requested span by sliding it into range rather than truncating
 * — so panning past an edge parks the window flush against that edge at the same
 * zoom level (the stock-app feel). The span is floored at ``MIN_VISIBLE_ROWS``
 * and capped at ``rowCount``.
 */
export function clampWindow(w: IndexWindow, rowCount: number): IndexWindow {
  if (rowCount <= 0) return { start: 0, end: 0 };
  const maxIdx = rowCount - 1;
  let span = w.end - w.start + 1;
  if (span < MIN_VISIBLE_ROWS) span = MIN_VISIBLE_ROWS;
  if (span > rowCount) span = rowCount;

  let start = w.start;
  let end = start + span - 1;
  if (start < 0) {
    start = 0;
    end = span - 1;
  }
  if (end > maxIdx) {
    end = maxIdx;
    start = maxIdx - span + 1;
  }
  if (start < 0) start = 0;
  return { start, end };
}

/**
 * Narrow (``factor`` < 1) or widen (``factor`` > 1) the window around
 * ``focalIndex`` — the row under the cursor stays put (the stock-app invariant):
 * its fractional position within the window is held fixed while the span scales.
 * Always makes at least one row of progress so a button/wheel tick never
 * dead-ends on rounding, and never drops below ``MIN_VISIBLE_ROWS`` / above
 * ``rowCount``.
 */
export function zoomAround(w: IndexWindow, focalIndex: number, factor: number, rowCount: number): IndexWindow {
  if (rowCount <= MIN_VISIBLE_ROWS) return fitWindow(rowCount);
  const curCount = visibleCount(w);

  let nextCount = Math.round(curCount * factor);
  nextCount = Math.max(MIN_VISIBLE_ROWS, Math.min(rowCount, nextCount));
  // Guarantee progress despite rounding (e.g. a 2-row window * 0.9 rounds to 2).
  if (factor < 1 && nextCount >= curCount) nextCount = Math.max(MIN_VISIBLE_ROWS, curCount - 1);
  if (factor > 1 && nextCount <= curCount) nextCount = Math.min(rowCount, curCount + 1);

  const clampedFocal = Math.max(w.start, Math.min(w.end, focalIndex));
  const f = curCount > 1 ? (clampedFocal - w.start) / (curCount - 1) : 0.5;
  const start = Math.round(clampedFocal - f * (nextCount - 1));
  return clampWindow({ start, end: start + nextCount - 1 }, rowCount);
}

/**
 * Shift the window by ``delta`` rows (positive = later/future, negative =
 * earlier/past), clamped to the data bounds while preserving the current span.
 */
export function panBy(w: IndexWindow, delta: number, rowCount: number): IndexWindow {
  return clampWindow({ start: w.start + delta, end: w.end + delta }, rowCount);
}

/** The inclusive slice of ``rows`` the window renders. */
export function sliceRows<T>(rows: readonly T[], w: IndexWindow): T[] {
  return rows.slice(w.start, w.end + 1);
}

/**
 * Map a cursor fraction (``0`` = left edge … ``1`` = right edge of the plot) to
 * the absolute row index under it, for focal wheel-zoom. Out-of-range fractions
 * clamp to the window ends.
 */
export function focalIndexAt(w: IndexWindow, fraction: number): number {
  const f = Math.max(0, Math.min(1, fraction));
  return w.start + Math.round(f * (visibleCount(w) - 1));
}
